import { buildQueryParams } from '@/helpers/ApiHelper'
import type { IPaginatedResponse } from '@/misc/IPaginatedResponse'
import type { DynamicLinqFilter } from '@/models/Filtering/DynamicLinqFilter'
import type { ILocalControllerConfigurationApplicationStatusHistory } from '@/models/History/ILocalControllerConfigurationApplicationStatusHistory'
import { type ILocalControllerConnectionHistory } from '@/models/History/ILocalControllerConnectionHistory'
import { type ILocalController } from '@/models/ILocalController'
import type { IOkResponse } from '@/models/Responses/IOkResponse'
import { inject, injectable } from 'inversify'
import type IApiClient from '../IApiClient'
import { SERVICES_TYPES } from '../ServicesTypes'
import type ILocalControllerService from './ILocalControllerService'

@injectable()
export class LocalControllerService implements ILocalControllerService {
  private readonly apiClient: IApiClient
  private readonly localControllerUri: string = 'local-controllers'
  private readonly localControllerPrefix: string = 'local-controller'

  constructor(@inject(SERVICES_TYPES.ApiClient) apiClient: IApiClient) {
    this.apiClient = apiClient
  }

  async getLocalControllers(
    pageNumber?: number,
    pageSize?: number,
    statuses?: string[] | null,
    sortField?: string | null,
    isAscending?: boolean | null
  ): Promise<IPaginatedResponse<ILocalController>> {
    const queryString = buildQueryParams({
      pageNumber,
      pageSize,
      statuses,
      sortField,
      isAscending
    })

    return this.apiClient.get<IPaginatedResponse<ILocalController>>(`${this.localControllerUri}${queryString}`)
  }

  async filterLocalControllers(filter: DynamicLinqFilter, pageNumber: number, pageSize: number): Promise<IPaginatedResponse<ILocalController>> {
    return this.apiClient.post<IPaginatedResponse<ILocalController>>(`${this.localControllerUri}/filter?pageNumber=${pageNumber}&pageSize=${pageSize}`, filter)
  }

  async getBySerialNumber(serialNumber: string): Promise<ILocalController> {
    return this.apiClient.get<ILocalController>(`${this.localControllerUri}/${serialNumber}`)
  }

  async getLocalControllerConnectionHistory(serialNumber: string, pageNumber: number = -1, pageSize: number = -1): Promise<IPaginatedResponse<ILocalControllerConnectionHistory>> {
    return this.apiClient.get<IPaginatedResponse<ILocalControllerConnectionHistory>>(`${this.localControllerPrefix}/${serialNumber}/connections/history?pageNumber=${pageNumber}&pageSize=${pageSize}`)
  }

  async getLocalControllerConfigurationApplicationStatusHistoryAsync(
    serialNumber: string,
    pageNumber: number = -1,
    pageSize: number = -1
  ): Promise<IPaginatedResponse<ILocalControllerConfigurationApplicationStatusHistory>> {
    return this.apiClient.get<IPaginatedResponse<ILocalControllerConfigurationApplicationStatusHistory>>(
      `${this.localControllerPrefix}/${serialNumber}/configuration/history?pageNumber=${pageNumber}&pageSize=${pageSize}`
    )
  }

  async restartLocalControllerAsync(serialNumber: string): Promise<IOkResponse> {
    return this.apiClient.post(`${this.localControllerUri}/${serialNumber}/restart`, {})
  }

  async scheduleRestartLocalControllerAsync(serialNumber: string, scheduleAt: string): Promise<IOkResponse> {
    return this.apiClient.post(`${this.localControllerUri}/${serialNumber}/schedule-restart/${scheduleAt}`, {})
  }

  async forceRefreshLocalControllerAsync(serialNumber: string): Promise<IOkResponse> {
    return this.apiClient.post(`${this.localControllerUri}/${serialNumber}/force-refresh`, {})
  }

  async requestLocalControllerLogUploadAsync(serialNumber: string): Promise<IOkResponse> {
    return this.apiClient.post(`${this.localControllerUri}/${serialNumber}/log-upload`, {})
  }

  async getLocalControllerLatestLogsAsync(serialNumber: string): Promise<{ blob: Blob; fileName?: string }> {
    return this.apiClient.getBlob(`${this.localControllerUri}/${serialNumber}/logs/latest`)
  }

  async pingLocalControllerAsync(serialNumber: string): Promise<IOkResponse> {
    return this.apiClient.post(`${this.localControllerUri}/${serialNumber}/ping`, {})
  }

  async updateLocalControllerAsync(serialNumber: string): Promise<IOkResponse> {
    return this.apiClient.post(`${this.localControllerUri}/${serialNumber}/update`, {})
  }

  async scheduleUpdateLocalControllerAsync(serialNumber: string, scheduleAt: string): Promise<IOkResponse> {
    return this.apiClient.post(`${this.localControllerUri}/${serialNumber}/update/${scheduleAt}`, {})
  }
}
